// Lista de leads CRM — /crm/leads (Story 11.2)
// - useInfiniteQuery com filtros (status/temperatura/origem/SaaS/overdue) + busca debounced
// - Paginação prev/next por cursor (DR-032), reuso de <Pagination>
// - Skeleton no loading inicial (AC10), empty/error states
// - Ações (criar/descartar) escondidas pra quem não tem permissão de escrita (AC8)
// - Descarte via modal com motivo obrigatório (AC6/AC10)

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, Target, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import type {
  CrmLeadOriginSchema,
  CrmLeadStatusSchema,
  CrmLeadTemperatureSchema,
} from '@control-center/shared';

import { EmptyState as RichEmptyState } from '../../../components/common/EmptyState';
import { Pagination } from '../../../components/common/Pagination';
import { PillButton } from '../../../components/clients/PillButton';
import { Select } from '../../../components/clients/form-primitives';
import { DiscardLeadDialog } from '../../../components/crm/DiscardLeadDialog';
import {
  useCrmLeadsInfinite,
  useDiscardCrmLead,
  type CrmLeadsListFilters,
} from '../../../hooks/use-crm-leads';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmLeadEntity } from '../../../lib/crm-leads-api';

const STATUS_FILTERS: { value: '' | CrmLeadStatusSchema; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'novo', label: 'Novo' },
  { value: 'qualificando', label: 'Qualificando' },
  { value: 'qualificado', label: 'Qualificado' },
  { value: 'convertido', label: 'Convertido' },
  { value: 'descartado', label: 'Descartado' },
];

const TEMPERATURE_FILTERS: { value: '' | CrmLeadTemperatureSchema; label: string }[] = [
  { value: '', label: 'Toda temperatura' },
  { value: 'quente', label: 'Quente' },
  { value: 'morno', label: 'Morno' },
  { value: 'frio', label: 'Frio' },
];

const ORIGIN_FILTERS: { value: '' | CrmLeadOriginSchema; label: string }[] = [
  { value: '', label: 'Todas as origens' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'indicacao', label: 'Indicação' },
  { value: 'trafego_pago', label: 'Tráfego pago' },
  { value: 'site', label: 'Site' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'evento', label: 'Evento' },
  { value: 'prospeccao_ativa', label: 'Prospecção ativa' },
  { value: 'outro', label: 'Outro' },
];

const TEMPERATURE_BADGE: Record<CrmLeadTemperatureSchema, string> = {
  quente: 'border-bad/40 text-bad',
  morno: 'border-warn/40 text-warn',
  frio: 'border-line text-muted',
};

const STATUS_LABEL: Record<CrmLeadStatusSchema, string> = {
  novo: 'Novo',
  qualificando: 'Qualificando',
  qualificado: 'Qualificado',
  convertido: 'Convertido',
  descartado: 'Descartado',
};

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export function CrmLeadsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | CrmLeadStatusSchema>('');
  const [temperatureFilter, setTemperatureFilter] = useState<'' | CrmLeadTemperatureSchema>('');
  const [originFilter, setOriginFilter] = useState<'' | CrmLeadOriginSchema>('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const debouncedQuery = useDebouncedValue(query, 300);

  const filters: CrmLeadsListFilters = useMemo(() => {
    const f: CrmLeadsListFilters = {};
    if (debouncedQuery.trim()) f.q = debouncedQuery.trim();
    if (statusFilter) f.status = statusFilter;
    if (temperatureFilter) f.temperature = temperatureFilter;
    if (originFilter) f.origin = originFilter;
    if (overdueOnly) f.overdue_contact = true;
    return f;
  }, [debouncedQuery, statusFilter, temperatureFilter, originFilter, overdueOnly]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCrmLeadsInfinite(filters);

  const [pageIndex, setPageIndex] = useState(0);
  useEffect(() => setPageIndex(0), [filters]);

  const pages = useMemo(() => data?.pages ?? [], [data]);
  const safeIndex = Math.min(pageIndex, Math.max(pages.length - 1, 0));
  const currentPage = pages[safeIndex];
  const leads = currentPage?.items ?? [];
  const total = currentPage?.meta.total ?? null;

  const rangeStart = useMemo(
    () =>
      leads.length === 0
        ? 0
        : pages.slice(0, safeIndex).reduce((acc, p) => acc + p.items.length, 0) + 1,
    [pages, safeIndex, leads.length],
  );
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + leads.length - 1;

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < pages.length - 1 || Boolean(hasNextPage);

  const goNext = (): void => {
    if (safeIndex < pages.length - 1) {
      setPageIndex(safeIndex + 1);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().then((res) => {
        if (!res.isError) setPageIndex(safeIndex + 1);
      });
    }
  };

  const hasActiveFilters =
    !!debouncedQuery || !!statusFilter || !!temperatureFilter || !!originFilter || overdueOnly;

  // Descarte
  const discard = useDiscardCrmLead();
  const [discardTarget, setDiscardTarget] = useState<CrmLeadEntity | null>(null);

  const handleDiscard = (reason: string): void => {
    if (!discardTarget) return;
    discard.mutate(
      { id: discardTarget.id, payload: { discard_reason: reason } },
      {
        onSuccess: (lead) => {
          toast.success('Lead descartado.', { description: lead.name });
          setDiscardTarget(null);
        },
        onError: (err) => {
          toast.error('Não conseguimos descartar.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          });
        },
      },
    );
  };

  const clearFilters = (): void => {
    setQuery('');
    setStatusFilter('');
    setTemperatureFilter('');
    setOriginFilter('');
    setOverdueOnly(false);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
            Leads
          </h1>
          <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
            O começo do funil comercial — rastreie cada contato antes de virar cliente.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
          {canWrite && (
            <PillButton
              solid
              onClick={() => navigate('/crm/leads/new')}
              icon={<Plus size={14} strokeWidth={2} />}
            >
              Novo lead
            </PillButton>
          )}
        </div>
      </header>

      {/* Filtros */}
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px_180px_180px]">
          <div className="relative">
            <Search
              size={14}
              strokeWidth={2}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, empresa, telefone ou e-mail…"
              aria-label="Buscar leads"
              className="h-10 w-full rounded-[10px] border border-line bg-panel pl-9 pr-3 text-[13px] text-ink placeholder:text-muted-3 outline-none transition-colors focus:border-line-2"
            />
          </div>
          <Select
            aria-label="Filtrar por status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | CrmLeadStatusSchema)}
          >
            {STATUS_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filtrar por temperatura"
            value={temperatureFilter}
            onChange={(e) => setTemperatureFilter(e.target.value as '' | CrmLeadTemperatureSchema)}
          >
            {TEMPERATURE_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filtrar por origem"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value as '' | CrmLeadOriginSchema)}
          >
            {ORIGIN_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex w-fit cursor-pointer items-center gap-2 text-[12px] text-ink-2">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-line bg-panel accent-ink"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
          />
          Só com próximo contato vencido
        </label>
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <LeadTableSkeleton />
      ) : isError ? (
        <ErrorState
          message={extractErrorMessage(error, 'Não conseguimos carregar os leads.')}
          onRetry={() => refetch()}
        />
      ) : leads.length === 0 ? (
        hasActiveFilters ? (
          <FilteredEmpty onClear={clearFilters} />
        ) : (
          <RichEmptyState
            icon={Target}
            title="Sem leads ainda"
            description="Cadastre o primeiro lead pra começar a rastrear o funil comercial — em vez de planilha ou WhatsApp solto."
            bullets={[
              'Origem, temperatura e potencial em cada lead',
              'Aviso de duplicata ao cadastrar (não bloqueia)',
              'Próximo contato com lembrete de vencido',
            ]}
            primaryAction={
              canWrite
                ? {
                    label: 'Cadastrar primeiro lead',
                    icon: <Plus size={14} strokeWidth={2} />,
                    onClick: () => navigate('/crm/leads/new'),
                  }
                : undefined
            }
          />
        )
      ) : (
        <>
          <LeadTable
            leads={leads}
            canWrite={canWrite}
            onOpen={(lead) => navigate(`/crm/leads/${lead.id}`)}
            onDiscard={(lead) => setDiscardTarget(lead)}
          />
          <Pagination
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => setPageIndex(Math.max(safeIndex - 1, 0))}
            onNext={goNext}
            isLoading={isFetchingNextPage}
            noun="lead"
          />
        </>
      )}

      {discardTarget && (
        <DiscardLeadDialog
          open={!!discardTarget}
          onOpenChange={(open) => {
            if (!open) setDiscardTarget(null);
          }}
          leadName={discardTarget.name}
          isPending={discard.isPending}
          onConfirm={handleDiscard}
        />
      )}
    </div>
  );
}

// =============================================================================
// Tabela
// =============================================================================

interface LeadTableProps {
  leads: CrmLeadEntity[];
  canWrite: boolean;
  onOpen: (lead: CrmLeadEntity) => void;
  onDiscard: (lead: CrmLeadEntity) => void;
}

function LeadTable({ leads, canWrite, onOpen, onDiscard }: LeadTableProps): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-line text-left text-[11px] uppercase tracking-wider text-muted-3">
            <th className="px-4 py-3 font-medium">Nome</th>
            <th className="px-4 py-3 font-medium">Empresa</th>
            <th className="px-4 py-3 font-medium">Origem</th>
            <th className="px-4 py-3 font-medium">Temperatura</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr
              key={lead.id}
              onClick={() => onOpen(lead)}
              className="cursor-pointer border-b border-line/60 transition-colors last:border-0 hover:bg-panel-2"
            >
              <td className="px-4 py-3 font-medium text-ink">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(lead);
                  }}
                  className="text-left transition-colors hover:text-link"
                >
                  {lead.name}
                </button>
              </td>
              <td className="px-4 py-3 text-muted">{lead.company ?? '—'}</td>
              <td className="px-4 py-3 text-muted-2">{originLabel(lead.origin)}</td>
              <td className="px-4 py-3">
                <span
                  className={`inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[11px] ${TEMPERATURE_BADGE[lead.temperature]}`}
                >
                  {temperatureLabel(lead.temperature)}
                </span>
              </td>
              <td className="px-4 py-3 text-muted-2">{STATUS_LABEL[lead.status]}</td>
              <td className="px-4 py-3 text-right">
                {canWrite && lead.status !== 'descartado' && lead.status !== 'convertido' && (
                  <button
                    type="button"
                    aria-label={`Descartar lead ${lead.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDiscard(lead);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-bad/10 hover:text-bad"
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadTableSkeleton(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <div className="flex flex-col divide-y divide-line/60">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-[14px]">
            <div className="h-3 w-40 animate-pulse rounded bg-panel-2" />
            <div className="h-3 w-28 animate-pulse rounded bg-panel-2" />
            <div className="ml-auto h-3 w-16 animate-pulse rounded bg-panel-2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel py-12 text-center">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Nenhum lead bate com esses filtros.
      </h2>
      <p className="mx-auto mt-1 max-w-[360px] text-[12px] text-muted">
        Tente afrouxar a busca ou limpar os filtros.
      </p>
      <div className="mt-4 flex justify-center">
        <PillButton onClick={onClear}>Limpar filtros</PillButton>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não conseguimos carregar os leads.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}

const ORIGIN_LABELS: Record<CrmLeadOriginSchema, string> = {
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  indicacao: 'Indicação',
  trafego_pago: 'Tráfego pago',
  site: 'Site',
  landing_page: 'Landing page',
  evento: 'Evento',
  prospeccao_ativa: 'Prospecção ativa',
  outro: 'Outro',
};

function originLabel(origin: CrmLeadOriginSchema): string {
  return ORIGIN_LABELS[origin] ?? origin;
}

function temperatureLabel(t: CrmLeadTemperatureSchema): string {
  return t === 'quente' ? 'Quente' : t === 'morno' ? 'Morno' : 'Frio';
}
